import { versionedPose } from '../../util/poseAsset';
export interface Walker {
  id: number;
  name: string;
  nameColors?: string[];
  nameNeon?: boolean;
  titleId?: number;
  vipExpiresAt?: number;
  svip?:boolean;svipExpiresAt?:number;
  level: number;
  village: number;
  model: number;
  serverId: number;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  startedAt: number;
  duration: number;
  facing: number;
  actionAnimation?: string;
  actionUntil?: number;
}
interface Frame {
  filename?: string;
  image?: HTMLImageElement;
  frame: { x: number; y: number; w: number; h: number };
  sourceSize: { w: number; h: number };
  spriteSourceSize: { x: number; y: number };
  rotated?: boolean;
}
export interface Animation {
  image: HTMLImageElement;
  frames: Frame[];
}
export interface Model {
  idle: Animation;
  walk: Animation;
  height: number;
  actions?: Record<string, Animation>;
}
export async function loadAtlas(folder: string, id: string): Promise<Animation> {
  const base = `${process.env.REACT_APP_CDN_PATH ?? ''}actors/${folder}/`;
  const response = await fetch(`${base}${id}.json`);
  if (!response.ok) throw new Error('village_back_assets');
  const json = await response.json(),
    texture = json.textures[0];
  return { image: await loadPicture(`${base}${texture.image}`), frames: texture.frames };
}
export async function loadVillageBoss(): Promise<Model> {
  const idle = await loadAtlas('pets/34053', '999'),
    attack = await loadAtlas('pets/34053', '52');
  return { idle, walk: idle, height: Math.max(...idle.frames.map((frame) => frame.sourceSize.h)), actions: { '52': attack } };
}
const models = new Map<number, Promise<Model>>();
async function loadCombinedVillageModel(model: number): Promise<Model> {
  const base = `${process.env.REACT_APP_CDN_PATH ?? ''}actorsV2/people/${model}/`;
  const response = await fetch(`${base}avatar.json`);
  if (!response.ok) throw new Error('village_back_assets');
  const atlas = await response.json();
  const frames: Frame[] = [];
  for (const texture of atlas.textures ?? []) {
    const image = await loadPicture(`${base}${texture.image}`);
    frames.push(...texture.frames.map((frame: Frame) => ({ ...frame, image })));
  }
  const animation = (id: string): Animation | undefined => {
    const selected = frames.filter(frame => frame.filename?.split('/')[0] === id)
      .sort((a, b) => Number(a.filename?.split('/').pop()) - Number(b.filename?.split('/').pop()));
    return selected.length ? { image: selected[0].image!, frames: selected } : undefined;
  };
  const idle = animation('999');
  if (!idle) throw new Error('village_back_assets');
  const walk = animation('55') ?? idle;
  return { idle, walk, height: Math.max(...[...idle.frames, ...walk.frames].map(f => f.sourceSize.h)) };
}

async function loadVillagePose(model: number): Promise<Model> {
  const image = await loadPicture(versionedPose(`${process.env.REACT_APP_CDN_PATH ?? ''}poses/${model}.png`));
  const width = image.naturalWidth, height = image.naturalHeight;
  if (!width || !height) throw new Error('village_back_assets');
  const idle = { image, frames: [{ frame: { x: 0, y: 0, w: width, h: height }, sourceSize: { w: width, h: height }, spriteSourceSize: { x: 0, y: 0 } }] };
  return { idle, walk: idle, height };
}
export function loadPicture(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('village_back_assets'));
    image.src = src;
  });
}
export function loadVillageModel(model: number): Promise<Model> {
  if (!models.has(model)) {
    const base = `${process.env.REACT_APP_CDN_PATH ?? ''}actors/people/${model}/`;
    const animation = async (id: string): Promise<Animation> => {
      const response = await fetch(`${base}${id}.json`);
      if (!response.ok) throw new Error('village_back_assets');
      const json = await response.json(),
        texture = json.textures[0];
      const image = await loadPicture(`${base}${texture.image}`);
      return { image, frames: texture.frames };
    };
    models.set(
      model,
      Promise.all([
        animation('999'),
        animation('55').catch(() => null),
        ...['52', '6', '57'].map((id) =>
          animation(id)
            .then((value) => ({ id, value }))
            .catch(() => null)
        ),
      ])
        .then(([idleValue, walkValue, ...extra]) => {
          const idle = idleValue as Animation,
            walk = (walkValue ?? idleValue) as Animation,
            actions: Record<string, Animation> = {};
          extra.forEach((entry) => {
            if (entry && 'id' in entry) actions[entry.id] = entry.value;
          });
          return { idle, walk, height: Math.max(...[...idle.frames, ...walk.frames].map((f) => f.sourceSize.h)), actions };
        })
        .catch(() => loadCombinedVillageModel(model))
        .catch(() => loadVillagePose(model))
        .catch((error) => {
          models.delete(model);
          throw error;
        })
    );
  }
  return models.get(model)!;
}
export function walkerPosition(player: Walker, now: number) {
  const progress = player.duration ? Math.min(1, Math.max(0, (now - player.startedAt) / player.duration)) : 1;
  return {
    x: player.x + (player.targetX - player.x) * progress,
    y: player.y + (player.targetY - player.y) * progress,
    moving: progress < 1,
  };
}
export function drawWalker(
  context: CanvasRenderingContext2D,
  player: Walker,
  model: Model,
  now: number,
  renderPosition?: { x: number; y: number; moving: boolean }
) {
  const position = renderPosition ?? walkerPosition(player, now),
    animation =
      player.actionUntil && player.actionUntil > now && player.actionAnimation && model.actions?.[player.actionAnimation]
        ? model.actions[player.actionAnimation]
        : position.moving
        ? model.walk
        : model.idle;
  const frame =
    animation.frames[
      player.actionAnimation === '57' && player.actionUntil && player.actionUntil > now
        ? animation.frames.length - 1
        : Math.floor(now / 85) % animation.frames.length
    ];
  const scale = 90 / model.height;
  context.save();
  context.translate(position.x, position.y);
  context.scale(player.facing > 0 ? -scale : scale, scale);
  // The same atlas frames as combat, anchored at the actor's feet.
  if (frame.rotated) {
    const x = -frame.sourceSize.w / 2 + (frame.spriteSourceSize?.x ?? 0);
    const y = -frame.sourceSize.h + (frame.spriteSourceSize?.y ?? 0);
    context.translate(x, y + frame.frame.w);
    context.rotate(-Math.PI / 2);
    context.drawImage(frame.image ?? animation.image, frame.frame.x, frame.frame.y, frame.frame.w, frame.frame.h, 0, 0, frame.frame.w, frame.frame.h);
  } else
    context.drawImage(
      frame.image ?? animation.image,
      frame.frame.x,
      frame.frame.y,
      frame.frame.w,
      frame.frame.h,
      -frame.sourceSize.w / 2 + (frame.spriteSourceSize?.x ?? 0),
      -frame.sourceSize.h + (frame.spriteSourceSize?.y ?? 0),
      frame.frame.w,
      frame.frame.h
    );
  context.restore();
}
